import { useQuery } from '@tanstack/react-query';

import type { PricingTeamPlanSummaryDto } from '@/api/core-api/types.gen';

import { PricingPlanComponentCapability } from '@/api/core-api/types.gen';
import { teamPlanSummaryQueryOptions } from '@/routes/researchers/_authenticated/projects/$projectId/-launch/team-pricing-version';

export type TeamCapabilities = {
  hasPastParticipant: boolean;
  hasNda: boolean;
  /**
   * Whether the team may use the Participant Database (browse + re-invite past
   * paid participants). Unlike the other two, this defaults CLOSED: the v4
   * team-respondents endpoints enforce the capability off the active plan, so a
   * team with no plan (or a plan without it) has no working API to show. The
   * database page / CTA fall back to the legacy dashboard when this is false.
   */
  hasParticipantDatabase: boolean;
};

/**
 * Default returned when the query is disabled (draft mode with no teamId)
 * or when the team has no pricing plan attached (legacy teams). The two launch
 * capabilities default open (defaulting-open is safe for those optional
 * features); `hasParticipantDatabase` defaults closed — see its doc above.
 */
const UNRESTRICTED: TeamCapabilities = {
  hasPastParticipant: true,
  hasNda: true,
  hasParticipantDatabase: false,
};

/**
 * Normalize a value that may be a single item or an array into an array.
 *
 * Needed because PricingTeamPlanPartialForSummaryDto types capabilities as
 * singular (PricingPlanComponentCapability) while the API returns arrays.
 */
function toArray<T>(value: T | T[] | undefined): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Derive the team's capability booleans from a raw plan summary. The single
 * source of truth for "what can this team do" — shared by the `useTeamCapabilities`
 * hook (CTA gating) and route `beforeLoad` guards (access gating) so the two
 * cannot silently diverge. Returns the unrestricted default when there is no
 * plan attached (see `UNRESTRICTED`).
 */
export function deriveTeamCapabilities(
  summary: PricingTeamPlanSummaryDto | undefined,
): TeamCapabilities {
  if (!summary?.plan) return UNRESTRICTED;
  const caps = new Set([
    ...toArray(summary.plan.teamCapabilities),
    ...toArray(summary.plan.orgCapabilities),
  ]);
  return {
    hasPastParticipant: caps.has(
      PricingPlanComponentCapability.PAST_PARTICIPANT,
    ),
    hasNda: caps.has(PricingPlanComponentCapability.NDA),
    hasParticipantDatabase: caps.has(
      PricingPlanComponentCapability.PARTICIPANT_DATABASE,
    ),
  };
}

/**
 * Fetches the team's pricing plan capabilities and returns them as a
 * strongly-typed boolean struct. Disabled when `teamId` is missing, in
 * which case the returned value is the unrestricted default.
 */
export function useTeamCapabilities(
  teamId: string | undefined,
): TeamCapabilities {
  const { data = UNRESTRICTED } = useQuery({
    ...teamPlanSummaryQueryOptions(teamId),
    select: deriveTeamCapabilities,
  });
  return data;
}
